import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  InputLabel,
  ModalProps,
  Stack,
  TextField,
  Typography,
  DialogActions,
  DialogContentText,
} from '@mui/material'
import { useQueryClient } from '@tanstack/react-query'
import { useFormik } from 'formik'
import isNil from 'lodash/isNil'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { AssigneeAutocomplete, AssigneeSourceType } from '@omnea-app/fe-users-and-groups/assignees/AssigneeAutocomplete'
import { DatePicker } from '@omnea-app/frontend/components/shared/form/date-picker/DatePicker'
import { EditableDropdownField } from '@omnea-app/frontend/components/shared/ui/editable-fields/EditableDropdownField'
import { palette } from '@omnea-app/omui/private/palette'
import { useRequestComments } from '../comments/api/useRequestComments'
import { PauseRequestFormValues, PauseRequestFormValidationSchema, initialFormValues } from './formHelpers'
import { IntegrationWarningModal } from './IntegrationWarningModal'
import { useGetPauseReasons } from './useGetPauseReasons'
import { useInsertPauseReason } from './useInsertPauseReason'
import { usePauseRequest, Pause } from './usePauseRequest'

interface Props {
  requestId: string
  open: boolean
  modalProps?: Partial<Omit<ModalProps, 'role'>>
  closeModal?: () => void
}

export const PauseModal = ({ open, requestId, closeModal, modalProps }: Props) => {
  const { t } = useTranslation('requests')
  const queryClient = useQueryClient()
  const { addPauseComment } = useRequestComments({ requestId })
  const { mutateAsync: insertPauseReason } = useInsertPauseReason()
  const { data: pauseReasons } = useGetPauseReasons()
  const pauseReasonsForDropdown = useMemo(() => pauseReasons?.map(({ id, text }) => ({ id, label: text })) || [], [pauseReasons])
  const [runningIntegrationSteps, setRunningIntegrationSteps] = useState([])

  const form = useFormik<PauseRequestFormValues>({
    initialValues: initialFormValues,
    onSubmit: (values) =>
      pauseRequest({
        requestId,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
        assigneeId: values.assignee!.id,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
        reasonId: values.reason!.id,
        note: values.note,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- FIXME: bulk migration to add error
        reminderDate: values.reminderDate!,
      }),
    validationSchema: toFormikValidationSchema(PauseRequestFormValidationSchema),
    validateOnChange: false,
  })

  const { values, setFieldValue, handleSubmit } = form

  const handlePauseSuccess = ({ pause_reason, note }: Pause) => {
    void addPauseComment({ reason: pause_reason, note })
    closeModal?.()
  }
  const handlePauseError = (err: any) => {
    if (err.detail?.integrationSteps) {
      // Integration steps are running
      closeModal?.()
      setRunningIntegrationSteps(err.detail.integrationSteps)
    }
  }
  const { mutate: pauseRequest, isPending } = usePauseRequest({ onSuccess: handlePauseSuccess, onError: handlePauseError })

  const addNewReasonChoice = useCallback(
    async (reason: string) => {
      const existingReasonWithLabel = pauseReasonsForDropdown.find(({ label }) => label === reason)
      if (existingReasonWithLabel) {
        // User has typed reason that already exists
        return existingReasonWithLabel.id
      }
      const { reason: newReason } = await insertPauseReason({ reason })
      if (!newReason) {
        // Error inserting reason
        return null
      }
      // Update both dropdown and query cache
      pauseReasonsForDropdown.push({ id: newReason.id, label: newReason.text })
      void queryClient.setQueryData(['pause-reasons'], (existingReasons: { id: string; text: string }[]) => {
        return [...existingReasons, newReason]
      })
      return newReason.id
    },
    [queryClient, insertPauseReason, pauseReasonsForDropdown]
  )

  return (
    <>
      <Dialog open={open} onClose={closeModal} fullWidth maxWidth="sm" {...modalProps}>
        <DialogTitle>{t('pause.modal-header')}</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 4 }}>{t('pause.modal-description')}</DialogContentText>
          <Stack spacing={5}>
            <Stack>
              <AssigneeAutocomplete
                label={t('pause.assignee')}
                required
                values={values.assignee ? [values.assignee] : []}
                type={AssigneeSourceType.USERS}
                allowSingleAssigneeOnly
                onChange={([newAssignee]) => setFieldValue('assignee', newAssignee)}
                title={t('create-brand.access-owner')}
                error={form.errors.assignee ? 'empty_assignees' : undefined}
                helperText={form.errors.assignee && t(form.errors.assignee)}
              />
              <Typography variant="HelperText" color="neutralLight.900" sx={{ mt: 1 }}>
                {t('pause.assignee-description')}
              </Typography>
            </Stack>
            <Stack>
              <InputLabel required>{t('pause.reminder-date')}</InputLabel>
              <DatePicker
                fullWidth
                value={values.reminderDate}
                onChange={(newValue) => setFieldValue('reminderDate', !isNil(newValue) ? new Date(newValue) : undefined)}
                textFieldProps={{
                  error: !!form.errors.reminderDate,
                  helperText: form.errors.reminderDate && t(form.errors.reminderDate),
                }}
              />
            </Stack>
            <Stack>
              <InputLabel required>{t('pause.reason')}</InputLabel>
              <EditableDropdownField
                sx={{
                  '& fieldset': {
                    border: `1px solid ${form.errors.reason ? palette.error.main : 'rgba(0,0,0,0.23)'} !important`,
                  },
                  '&:hover fieldset': {
                    border: `1px solid ${form.errors.reason ? palette.error.main : palette.text.primary} !important`,
                  },
                  '.Mui-focused fieldset': {
                    border: `1px solid ${palette.purple[300]} !important`,
                  },
                }}
                value={values.reason}
                addNewChoice={(label) => addNewReasonChoice(label)}
                save={async (newId) => {
                  void setFieldValue(
                    'reason',
                    pauseReasonsForDropdown.find(({ id }: { id: string }) => id === newId)
                  )
                }}
                choices={pauseReasonsForDropdown || []}
                error={!!form.errors.reason}
                helperText={form.errors.reason && t(form.errors.reason)}
              />
            </Stack>
            <Stack>
              <TextField
                label={t('pause.note')}
                variant="outlined"
                value={values.note}
                onChange={(e) => setFieldValue('note', e.target.value)}
                multiline
                minRows={3}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeModal} color="neutral" variant="outlined">
            {t('pause.close')}
          </Button>
          <Button variant="contained" color="secondary" onClick={() => handleSubmit()} loading={isPending}>
            {t('pause.pause-request')}
          </Button>
        </DialogActions>
      </Dialog>
      <IntegrationWarningModal
        open={runningIntegrationSteps.length > 0}
        onClose={() => setRunningIntegrationSteps([])}
        integrationSteps={runningIntegrationSteps}
      />
    </>
  )
}
